import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  ShoppingBag, ArrowLeft, ShoppingCart, Download, FileIcon, CheckCircle,
  Printer, Truck, Monitor, ShieldCheck, Pencil, Trash2, Plus, Save,
  Loader2, X, Star, Check, Upload, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';
import Price from '../components/Price';
import ProductReviews from '../components/ProductReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AVAILABLE_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const AVAILABLE_COLORS = [
  { name: 'White', value: '#FFFFFF' }, { name: 'Black', value: '#1a1a1a' },
  { name: 'Navy', value: '#1e3a5f' }, { name: 'Red', value: '#dc2626' }, { name: 'Gray', value: '#6b7280' },
];

const getImgSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('data:')) return img;
  return `data:image/jpeg;base64,${img}`;
};

export default function ProductDetailPage() {
  const { t, i18n } = useTranslation();
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [buying, setBuying] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [purchaseId, setPurchaseId] = useState(null);
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedColor, setSelectedColor] = useState('White');

  // Inline editing state
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const fileRef = useRef(null);

  const isOwner = product && user && (product.seller_id === user.id || user.role === 'admin');

  const loadProduct = useCallback(async () => {
    try {
      // If user clicked "Show original" we send an empty lang to bypass server-side translation
      const params = showOriginal ? { lang: '' } : undefined;
      const res = await api.get(`/products/${productId}`, params ? { params } : undefined);
      setProduct(res.data);
    }
    catch { toast.error('Product not found'); }
  }, [productId, showOriginal]);

  const checkPurchase = useCallback(async () => {
    try {
      const res = await api.get(`/downloads/check/${productId}`);
      setPurchased(res.data.purchased);
      setPurchaseId(res.data.purchase_id);
    } catch {}
  }, [productId]);

  useEffect(() => { loadProduct(); checkPurchase(); }, [loadProduct, checkPurchase]);

  // ── Inline edit helpers ──
  const startEdit = (field, currentValue) => {
    if (!isOwner) return;
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const cancelEdit = () => { setEditingField(null); setEditValue(''); };

  const saveField = async (field, value) => {
    setSaving(true);
    try {
      const payload = {};
      payload[field] = field === 'price' ? parseFloat(value) : value;
      await api.put(`/products/${productId}/edit`, payload);
      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`);
      setEditingField(null);
      await loadProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter' && field !== 'description') { e.preventDefault(); saveField(field, editValue); }
    if (e.key === 'Escape') cancelEdit();
  };

  // ── Image management ──
  const handleDeleteImage = async (idx) => {
    if ((product.images?.length || 0) <= 1) { toast.error('Must keep at least one image'); return; }
    try {
      await api.delete(`/products/${productId}/images/${idx}`);
      toast.success('Image removed');
      setActiveImage(0);
      await loadProduct();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/products/${productId}/images/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image added');
      await loadProduct();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleSetCover = async (idx) => {
    if (idx === 0) return;
    try {
      const order = product.images.map((_, i) => i);
      await api.put(`/products/${productId}/images/reorder`, { order, cover_index: idx });
      toast.success('Cover image updated');
      setActiveImage(0);
      await loadProduct();
    } catch (err) { toast.error('Failed to set cover'); }
  };

  const handleBuy = async () => {
    setBuying(true);
    try {
      const res = await api.post('/payments/checkout', {
        product_id: productId, origin_url: window.location.origin,
        pod_options: product?.is_design_product ? { size: selectedSize, color: selectedColor } : null
      });
      window.location.href = res.data.url;
    } catch { toast.error('Payment setup failed'); }
    finally { setBuying(false); }
  };

  if (!product) return <div className="text-center py-20 text-muted-foreground text-sm">Loading...</div>;

  const fileCount = product.file_count || product.download_files?.length || 0;
  const isPodProduct = product.is_design_product || product.category === 'design';
  const images = product.images || [];
  const mainImg = getImgSrc(images[activeImage] || images[0]);

  return (
    <div className="max-w-4xl mx-auto animate-in" data-testid="product-detail-page">
      <div className="flex items-center justify-between mb-6">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-marketplace">
          <ArrowLeft size={14} /> Back to Marketplace
        </Link>
        {isOwner && (
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold uppercase" data-testid="owner-edit-badge">
              <Pencil size={9} className="inline mr-1" />Editing Mode
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* ── Left: Images ── */}
        <div className="space-y-3">
          {/* Main image */}
          <div className="relative aspect-square bg-mist border border-border/40 overflow-hidden rounded-xl group" data-testid="main-product-image">
            {mainImg ? (
              <img src={mainImg} alt={product.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={48} className="text-muted-foreground/20" /></div>
            )}
            {/* Owner overlay controls */}
            {isOwner && images.length > 0 && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                {activeImage > 0 && (
                  <button onClick={() => handleSetCover(activeImage)} className="p-2.5 rounded-full bg-white/90 text-amber-600 hover:bg-amber-500 hover:text-white transition-colors shadow-lg" title="Set as cover">
                    <Star size={16} />
                  </button>
                )}
                <button onClick={() => handleDeleteImage(activeImage)} className="p-2.5 rounded-full bg-white/90 text-red-500 hover:bg-red-500 hover:text-white transition-colors shadow-lg" title="Delete image" data-testid="delete-main-image">
                  <Trash2 size={16} />
                </button>
              </div>
            )}
            {isOwner && activeImage === 0 && (
              <div className="absolute top-2.5 left-2.5 bg-amber-500 text-white text-[8px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">Cover</div>
            )}
          </div>

          {/* Gallery thumbnails */}
          <div className="grid grid-cols-5 gap-2">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`relative aspect-square bg-mist border-2 overflow-hidden rounded-lg transition-all group/thumb ${
                  activeImage === i ? 'border-amber-400 ring-1 ring-amber-200' : 'border-border/30 hover:border-amber-200'
                }`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={getImgSrc(img)} alt={`Preview ${i+1}`} className="w-full h-full object-cover" />
                {isOwner && (
                  <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover/thumb:opacity-100">
                    <Trash2 size={10} className="text-white" />
                  </div>
                )}
              </button>
            ))}
            {/* Upload button — always visible for owner */}
            {isOwner && (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="aspect-square border-2 border-dashed border-amber-200 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-amber-400 hover:bg-amber-50/30 transition-all"
                data-testid="upload-image-btn"
              >
                {uploading ? <Loader2 size={14} className="animate-spin text-amber-400" /> : <Plus size={16} className="text-amber-400" />}
                <span className="text-[7px] text-amber-500 font-medium uppercase">Add</span>
              </button>
            )}
            <input ref={fileRef} type="file" className="hidden" accept="image/*" onChange={handleUploadImage} />
          </div>
        </div>

        {/* ── Right: Details ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-mist text-foreground rounded-sm text-[10px] uppercase">{product.category}</Badge>
            {isPodProduct && <Badge className="bg-purple-500/20 text-purple-600 border-purple-500/30 text-[10px]"><Printer size={10} className="mr-1" /> Print-on-Demand</Badge>}
          </div>

          {/* ── Title (inline editable) ── */}
          {editingField === 'title' ? (
            <div className="mb-3" data-testid="edit-title-inline">
              <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => handleKeyDown(e, 'title')} autoFocus className="font-serif text-2xl h-12 border-amber-300" />
              <div className="flex gap-2 mt-2">
                <Button size="sm" disabled={saving} onClick={() => saveField('title', editValue)} className="bg-obsidian text-white text-[10px] h-7">
                  {saving ? <Loader2 size={10} className="animate-spin mr-1" /> : <Check size={10} className="mr-1" />} Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-[10px] h-7"><X size={10} className="mr-1" /> Cancel</Button>
              </div>
            </div>
          ) : (
            <h1
              onClick={() => startEdit('title', product.title)}
              className={`font-serif text-2xl md:text-3xl mb-2 ${isOwner ? 'cursor-pointer hover:bg-amber-50 hover:outline hover:outline-1 hover:outline-amber-200 rounded-lg px-1 -mx-1 transition-all' : ''}`}
              data-testid="product-title"
            >
              {product.title}
              {isOwner && <Pencil size={12} className="inline ml-2 text-amber-400 opacity-0 group-hover:opacity-100" />}
            </h1>
          )}

          {/* Translation banner — visible whenever the product's source language differs from the viewer's UI language */}
          {(() => {
            const viewerLang = (i18n.language || 'en').split('-')[0];
            const source = (product.source_language || product.original_language || 'en').split('-')[0];
            if (!source || source === viewerLang || isOwner) return null;
            return (
              <div
                className="inline-flex items-center gap-2 bg-amber-50/70 border border-amber-200/80 px-2.5 py-1 rounded-full text-[10px] text-amber-700 mb-3"
                data-testid="product-translation-banner"
              >
                <Languages size={11} />
                <span>
                  {showOriginal
                    ? (t('product.viewing_original') !== 'product.viewing_original' ? t('product.viewing_original', { lang: source.toUpperCase() }) : `Viewing original (${source.toUpperCase()})`)
                    : (t('product.translated_from') !== 'product.translated_from' ? t('product.translated_from', { lang: source.toUpperCase() }) : `Auto-translated from ${source.toUpperCase()}`)}
                </span>
                <button
                  onClick={() => setShowOriginal(v => !v)}
                  data-testid="product-translation-toggle"
                  className="underline hover:text-amber-900 font-medium"
                >
                  {showOriginal
                    ? (t('product.show_translation') !== 'product.show_translation' ? t('product.show_translation') : 'Show translation')
                    : (t('product.show_original') !== 'product.show_original' ? t('product.show_original') : 'Show original')}
                </button>
              </div>
            );
          })()}

          {/* ── Subtitle (inline editable) ── */}
          {editingField === 'subtitle' ? (
            <div className="mb-3" data-testid="edit-subtitle-inline">
              <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => handleKeyDown(e, 'subtitle')} autoFocus className="text-sm border-amber-300" placeholder="Subtitle" />
              <div className="flex gap-2 mt-2">
                <Button size="sm" disabled={saving} onClick={() => saveField('subtitle', editValue)} className="bg-obsidian text-white text-[10px] h-7"><Check size={10} className="mr-1" /> Save</Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-[10px] h-7"><X size={10} className="mr-1" /> Cancel</Button>
              </div>
            </div>
          ) : (
            product.subtitle || isOwner ? (
              <p
                onClick={() => startEdit('subtitle', product.subtitle)}
                className={`text-sm text-amber-700/70 font-light mb-3 italic ${isOwner ? 'cursor-pointer hover:bg-amber-50 rounded px-1 -mx-1 transition-all min-h-[20px]' : ''}`}
                data-testid="product-subtitle"
              >
                {product.subtitle || (isOwner ? 'Add subtitle...' : '')}
              </p>
            ) : null
          )}

          {/* ── Description (inline editable) ── */}
          {editingField === 'description' ? (
            <div className="mb-6" data-testid="edit-description-inline">
              <Textarea value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => handleKeyDown(e, 'description')} autoFocus className="text-sm min-h-[100px] border-amber-300 resize-none" />
              <div className="flex gap-2 mt-2">
                <Button size="sm" disabled={saving} onClick={() => saveField('description', editValue)} className="bg-obsidian text-white text-[10px] h-7"><Check size={10} className="mr-1" /> Save</Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-[10px] h-7"><X size={10} className="mr-1" /> Cancel</Button>
              </div>
            </div>
          ) : (
            <p
              onClick={() => startEdit('description', product.description)}
              className={`text-sm text-muted-foreground mb-6 leading-relaxed ${isOwner ? 'cursor-pointer hover:bg-amber-50 rounded-lg px-1 -mx-1 py-1 transition-all' : ''}`}
              data-testid="product-description"
            >
              {product.description}
            </p>
          )}

          {/* Specs */}
          {product.specs && (
            <div className="border border-amber-200/40 bg-amber-50/20 rounded-lg p-4 mb-6" data-testid="product-specs">
              <h3 className="text-xs uppercase tracking-wider text-amber-700 mb-3 font-medium flex items-center gap-1.5"><Monitor size={12} /> Product Details</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                {product.specs.source_resolution && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Source Resolution</p><p className="text-xs font-medium text-obsidian">{product.specs.source_resolution}</p></div>}
                {product.specs.resolution && !product.specs.source_resolution && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Resolution</p><p className="text-xs font-medium text-obsidian">{product.specs.resolution}</p></div>}
                {product.specs.format && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Format</p><p className="text-xs font-medium text-obsidian">{product.specs.format}</p></div>}
                {product.specs.dpi_range && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Print Quality</p><p className="text-xs font-medium text-obsidian">{product.specs.dpi_range}</p></div>}
                {product.specs.wall_sizes_included && <div><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Wall Sizes</p><p className="text-xs font-medium text-obsidian">{product.specs.wall_sizes_included} sizes included</p></div>}
                {product.specs.coverage_sizes && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Coverage</p><p className="text-xs font-medium text-obsidian">{product.specs.coverage_sizes}</p></div>}
                {product.specs.suitable_for && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Suitable For</p><p className="text-xs font-medium text-obsidian">{product.specs.suitable_for}</p></div>}
                {product.specs.tiling_compatibility && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Tiling</p><p className="text-xs font-medium text-obsidian">{product.specs.tiling_compatibility}</p></div>}
                {product.specs.delivery && <div className="col-span-2"><p className="text-[9px] text-muted-foreground uppercase tracking-wider">Delivery</p><p className="text-xs font-medium text-obsidian">{product.specs.delivery}</p></div>}
              </div>
            </div>
          )}

          {/* Creator Protection */}
          <div className="flex gap-2.5 p-3.5 bg-emerald-50/60 border border-emerald-200/40 rounded-lg mb-6" data-testid="product-protection-badge">
            <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-medium text-emerald-800">Creator Protection Enabled</p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                <span className="text-[9px] text-emerald-700/80">Secure File Delivery</span>
                <span className="text-[9px] text-emerald-700/80">Protected Downloads</span>
                <span className="text-[9px] text-emerald-700/80">Full Creator Ownership</span>
              </div>
            </div>
          </div>

          {/* Files */}
          {!isPodProduct && fileCount > 0 && (
            <div className="border border-border/40 p-4 mb-6 rounded-lg">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Included Files ({fileCount})</h3>
              <div className="space-y-2">
                {product.download_files?.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <FileIcon size={12} className="text-gold" /><span className="truncate">{f.name}</span><span className="text-muted-foreground">{f.mime_type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* POD */}
          {isPodProduct && (
            <div className="border border-purple-200 bg-purple-50/50 p-4 mb-6 rounded-lg">
              <h3 className="text-xs uppercase tracking-wider text-purple-700 mb-2 flex items-center gap-1"><Printer size={12} /> Custom Printed Product</h3>
              <p className="text-xs text-purple-600/80">Print-on-demand product. After purchase, we print and ship directly to you.</p>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Truck size={12} /> Estimated delivery: 5-7 business days</p>
            </div>
          )}

          {isPodProduct && !purchased && (
            <div className="border border-border/40 p-4 mb-6 space-y-4 rounded-lg">
              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Select Size</h3>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_SIZES.map(s => (
                    <button key={s} onClick={() => setSelectedSize(s)} className={`px-3 py-2 text-xs border rounded transition-all ${selectedSize === s ? 'border-gold bg-gold/10 text-gold font-medium' : 'border-border/40 hover:border-gold/50'}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Select Color</h3>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_COLORS.map(c => (
                    <button key={c.name} onClick={() => setSelectedColor(c.name)} className={`flex items-center gap-2 px-3 py-2 text-xs border rounded transition-all ${selectedColor === c.name ? 'border-gold bg-gold/10 font-medium' : 'border-border/40 hover:border-gold/50'}`}>
                      <span className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: c.value }} />{c.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Seller & Price */}
          <div className="border-t border-border/40 pt-4 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-8 w-8 border"><AvatarFallback className="bg-obsidian text-white text-[10px]">{(product.seller_name || 'S')[0]}</AvatarFallback></Avatar>
              <div><p className="text-xs font-medium">{product.seller_name}</p><p className="text-[10px] text-muted-foreground">Seller</p></div>
            </div>
            <div className="flex items-center justify-between">
              {/* ── Price (inline editable) ── */}
              {editingField === 'price' ? (
                <div className="flex items-center gap-2" data-testid="edit-price-inline">
                  <span className="text-lg text-muted-foreground">€</span>
                  <Input type="number" step="0.01" min="0.50" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => handleKeyDown(e, 'price')} autoFocus className="w-28 font-serif text-2xl h-10 border-amber-300" />
                  <Button size="sm" disabled={saving} onClick={() => saveField('price', editValue)} className="bg-obsidian text-white h-8 text-[10px]"><Check size={10} /></Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8 text-[10px]"><X size={10} /></Button>
                </div>
              ) : (
                <span
                  onClick={() => startEdit('price', String(product.price))}
                  className={`text-2xl font-serif text-gold ${isOwner ? 'cursor-pointer hover:bg-amber-50 rounded px-2 -mx-2 py-0.5 transition-all' : ''}`}
                  data-testid="product-price"
                >
                  <Price amountEur={product.price} />
                </span>
              )}
              <span className="text-xs text-muted-foreground">{product.purchases || 0} purchased</span>
            </div>
          </div>

          {/* CTA */}
          {isOwner ? (
            <div className="text-center py-3 border border-amber-200 bg-amber-50/30 rounded-lg" data-testid="owner-listing-notice">
              <p className="text-xs text-amber-700 font-medium">Your product — tap any field to edit</p>
              <p className="text-[10px] text-amber-600/70 mt-0.5">Changes save instantly to the marketplace</p>
            </div>
          ) : purchased ? (
            <div className="space-y-3" data-testid="already-purchased-section">
              <div className="flex items-center gap-2 justify-center py-3 border border-green-200 bg-green-50 rounded-lg">
                <CheckCircle size={16} className="text-green-600" /><span className="text-sm font-medium text-green-700">You own this product</span>
              </div>
              <Button data-testid="go-to-downloads-btn" onClick={() => navigate('/downloads')} className="w-full rounded-lg h-12 uppercase tracking-wider text-xs bg-gold text-obsidian hover:bg-obsidian hover:text-white transition-colors">
                <Download size={14} className="mr-2" /> Go to My Purchases
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Button data-testid="buy-product-btn" onClick={handleBuy} disabled={buying} className="w-full rounded-lg h-12 uppercase tracking-wider text-xs bg-obsidian hover:bg-gold hover:text-obsidian transition-colors">
                <ShoppingCart size={14} className="mr-2" />
                {buying ? 'Processing...' : <>Buy Now — <Price amountEur={product.price} /></>}
              </Button>
            </div>
          )}

          <div className="mt-4">
            <ShareButton title={product.title} description={product.description?.slice(0, 140)} />
          </div>
        </div>
      </div>

      {/* Multilingual reviews section — appears under the product for all viewers */}
      <ProductReviews productId={product.id} isOwner={isOwner} purchased={purchased} />
    </div>
  );
}
